import React, { FC, useEffect, useId, useState } from 'react';

import DropdownSmall from '@rover/icons/svg/dropdown-small.svg';
import { Box, Text } from '@rover/kibble/core';
import { ButtonIconSizeMap } from '@rover/kibble/core/Button/Button.common';
import useTheme from '@rover/kibble/themes/useTheme';
import { isAndroid, isiOS } from '@rover/rsdk/src/modules/Network/userAgent';

import { getAriaLabel, Props } from './ClickableInput.common';

const ClickableInput: FC<Props> = ({
  children,
  helperText,
  inputLabel,
  inputLabelSize,
  inputLabelColor,
  inputLabelMarginBottom,
  isError,
  onClick,
  icon = (
    <DropdownSmall
      width={ButtonIconSizeMap.default}
      height={ButtonIconSizeMap.default}
      fill="currentColor"
    />
  ),
  hideIcon,
  pointerCursor = false,
  readOnly = false,
}) => {
  const theme = useTheme();
  const buttonId = useId();
  const helperTextId = useId();
  const [showIcon, setShowIcon] = useState(false);

  useEffect(() => {
    setShowIcon(!hideIcon && !readOnly);
  }, [hideIcon, readOnly]);

  return (
    <>
      <Text
        fontWeight="semibold"
        mb={inputLabelMarginBottom ?? '1x'}
        sx={{
          display: 'block',
          pointerEvents: 'none',
        }}
        as="label"
        size={inputLabelSize ?? ['100', '200']}
        {...(inputLabelColor ? { textColor: inputLabelColor } : {})}
        htmlFor={buttonId}
      >
        {inputLabel}
      </Text>
      <Box
        id={buttonId}
        webAs="button"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        // Android and iOS screen readers will only read the `label` element and not the button value (`children`)
        aria-label={isAndroid() || isiOS() ? getAriaLabel(inputLabel, children) : undefined}
        aria-describedby={helperText ? helperTextId : undefined}
        aria-disabled={readOnly || undefined}
        data-testid="clickable-input"
        onClick={readOnly ? undefined : onClick}
        px="2x"
        width="100%"
        height="12x"
        borderRadius="secondary"
        border="none"
        background="primary"
        sx={{
          boxShadow: isError
            ? `0px 0px 0px 2px ${theme.colors.border.error.toString()}`
            : `0px 0px 0px 2px  ${theme.colors.border.primary.toString()}`,
          color: theme.textColor.secondary.toString(),
          // eslint-disable-next-line no-nested-ternary
          cursor: readOnly ? 'not-allowed' : pointerCursor ? 'pointer' : undefined,
          ...(readOnly
            ? {}
            : {
                '@media (pointer: fine)': {
                  '&:hover': {
                    backgroundColor: theme.background.secondary.toString(),
                  },
                },
              }),
        }}
      >
        {children}
        <Box
          width={ButtonIconSizeMap.default}
          height={ButtonIconSizeMap.default}
          flexShrink="0"
          display={showIcon ? 'block' : 'none'}
          data-testid="clickable-input-icon-wrapper"
        >
          {icon}
        </Box>
      </Box>
      {helperText && (
        <Text
          data-testid="helper-text"
          webProps={{ id: helperTextId }}
          size="100"
          textColor={isError ? 'error' : 'tertiary'}
          mt="2x"
          sx={{ display: 'block' }}
        >
          {helperText}
        </Text>
      )}
    </>
  );
};

export default ClickableInput;
